import startCase from 'lodash/startCase';
import { IJob } from 'types';
import { toTitleCase } from './strings';

export interface IItemWithJob {
  job: IJob;
  [key: string]: any;
}

export interface IItemWithJobs {
  jobs: IJob[];
  [key: string]: any;
}

/**
 * iterates over an array of objects that may have a job property
 * and returns an array of objects where each object's job property
 * of type Job or { title: string } has been converted to a string.
 *
 * if the object does not have a job property, it is returned as is.
 *
 * @param arr - an array of objects where each object may have a job
 *  property that is of type Job or { title: string }
 * @returns - an array of objects where each object has a job property
 *  that is a string
 */
export const parseJobToString = (arr: IItemWithJob[]) =>
  arr.map((item) => {
    if (!item.job?.title) return { ...item };

    return {
      ...item,
      job: item.job.title,
    };
  });

/**
 * iterates over an array of objects that may have a jobs property
 * and returns an array of objects where each object's jobs property
 * of type Job[] or { title: string }[] has been converted to a string[].
 *
 * if the object does not have a jobs property, it is returned as is.
 *
 * @param arr - an array of objects where each object may have a jobs
 * property that is of type Job[] or { title: string }[]
 * @returns - an array of objects where each object has a jobs property
 * that is a string[]
 */
export const parseJobsToStrings = (arr: IItemWithJobs[]) =>
  arr.map((item) => {
    if (!item.jobs?.length) return { ...item };

    return {
      ...item,
      jobs: item.jobs.map((job) => job.title),
    };
  });

/**
 * Formats a job title by converting it to title case and optionally appending a classification in parentheses.
 *
 * @param {string} title - The job title to format.
 * @param {string} [classification] - An optional classification to append after the job title.
 *                                     The classification will be converted to start case and enclosed in parentheses.
 * @returns {string} - The formatted job title, optionally followed by the classification in parentheses.
 */
export const formatJobTitle = (title: string, classification?: string): string => `${toTitleCase(title)}${classification ? ` (${startCase(classification)})` : ''}`;
